import React, { useEffect, useState } from 'react';
import fp from 'lodash/fp';
import bytes from 'bytes';
import { useLocation } from 'react-router-dom';
import { rawRequest, contentTypeJson } from '@factorin/rest';

import { wrapRequest } from 'Modules/core/api';
import { Label, Link, GridRow, GridColumn, Text, Icon, Tooltip, Note, LoadingIndicator } from 'Modules/ui2/components';
import help from 'Modules/ui2/icons/help.sprite.svg';
import download from 'Modules/ui2/icons/download.sprite.svg';
import { useSelector } from 'Modules/core/redux/utils';

import { RegistrationStep } from '../../RegistrationStep';
import styles from './index.module.scss';
import commonStyles from '../common.module.scss';


const eSigTooltip = 'Усиленная квалифицированная электронная подпись (УКЭП). Для прохождения регистрации должен быть установлен криптопровайдер, поддерживающий алгоритмы ГОСТ, и КриптоПРО Browser Plug-in.';
const proxyTooltip = 'Если подписант не ген. директор';

const getPublicLink = ({ Uid }, clientofficeUrl) => fp.pipe(fp.filter(fp.identity), fp.join(''))([
    clientofficeUrl,
    '/api/v1',
    `/companydocuments/public/${Uid}/download`,
]);

const PublicDocumentsList = () => {
    const [docs, setDocs] = useState(null);
    const clientofficeUrl = useSelector('landing.config.clientofficeUrl');
    useEffect(
        () => {
            if (clientofficeUrl) {
                wrapRequest(rawRequest, {
                    method: 'GET',
                    url: `${clientofficeUrl}/api/v1/companydocuments/public`,
                    headers: {
                        Accept: contentTypeJson,
                        'Accept-Language': 'ru',
                        'Content-Type': contentTypeJson,
                    },
                }).then(docs => fp.isArray(docs) && setDocs(docs));
            }
        },
        [clientofficeUrl],
    );
    if (!docs) {
        return (
            <>
                <Label component='h3' weight='demibold' textSize='21-24'>
                    Заранее ознакомьтесь с документами:
                </Label>
                <LoadingIndicator />
            </>
        );
    }

    return (
        <>
            <Label component='h3' weight='demibold' textSize='21-24'>
                Заранее ознакомьтесь с документами:
            </Label>
            <Text component='ul' textSize='18'>
                {docs.map(doc => (
                    <div key={doc.Uid}>
                        <Link download href={getPublicLink(doc, clientofficeUrl)} icon={download}>
                            {doc.DocumentTypeName}&nbsp;
                            <Note>
                                {doc.FileExtension.substr(1)},&nbsp;
                                {bytes(doc.FileSize, { decimalPlaces: 1 })}
                            </Note>
                        </Link>
                    </div>
                ))}
            </Text>
        </>
    );
};

const CommercialTerm = ({ title, description, clarification }) => (
    <div className={styles.commercialTerm}>
        <Label component='h3' weight='demibold' textSize='32'>
            {title}
        </Label>
        <Text component='div' textSize='18'>
            {description}
        </Text>
        <Label component='div' textSize='14' className={styles.termClarification}>
            {clarification}
        </Label>
    </div>
);

const commercialTerms = [
    {
        title: '12 000** ₽/год',
        description: '',
        clarification: '** не включает НДС',
    },
];

const DefaultCommercialTerms = () => (
    <GridRow className={styles.commercialTerms}>
        <GridColumn size='8'>
            <Label component='h3' weight='demibold' textSize='21-24'>
                Специальные условия для  участников, которые  подключаются к платформе в
                роли Поставщиков или Покупателей:
            </Label>
            <div className={styles.commercialTermItems}>
                {commercialTerms.map((element, index) => (
                    <CommercialTerm key={index} {...element} />
                ))}
            </div>
        </GridColumn>
    </GridRow>
);

const tanderCommercialTerms = [
    {
        title: 'Бесплатно *',
        description: 'при использовании только продукта "Маркетплейс Тандер"',
    },
    {
        title: '12 000** ₽/год',
        description: 'при использовании других продуктов платформы',
        clarification: '** не включает НДС',
    },
];

const TanderCommercialTerms = () => (
    <GridRow className={styles.commercialTerms}>
        <GridColumn size='10'>
            <Label component='h3' weight='demibold' textSize='21-24'>
                Специальные условия подключения к Платформе по
                продукту &quot;Маркетплейс Тандер&quot; для участников-поставщиков АО Тандер
            </Label>
            <div className={styles.commercialTermItems}>
                {tanderCommercialTerms.map((element, index) => (
                    <CommercialTerm key={index} {...element} />
                ))}
            </div>
            <Text component='div' textSize='14' className={styles.note}>
                * В случае если участник заключил лицензионный договор с ООО «Факторин»
                в первый раз не ранее <b>01 апреля 2021</b> г., обязанность по оплате лицензионного
                вознаграждения возникает в календарный месяц, в котором участник подключится
                к любому другому продукту Платформы помимо “Маркетплейс Тандер”.
                При этом лицензионное вознаграждение выплачивается в порядке, указанном в
                лицензионном договоре за 12 (двенадцать) месяцев, начиная с даты
                заключения лицензионного договора между участником и ООО «Факторин».
            </Text>
        </GridColumn>
    </GridRow>
);

const invoiceMarketCommercialTerms = [
    {
        title: 'Бесплатно *',
        description: 'до 1 сентября 2021 г., продукт "Уступка с верификацией по ЭДО"',
    },
];

const InvoiceMarketCommercialTerms = () => (
    <GridRow className={styles.commercialTerms}>
        <GridColumn size='10'>
            <Label component='h3' weight='demibold' textSize='21-24'>
                Специальные условия подключения к Платформе по
                продукту &quot;Уступка с верификацией по ЭДО&quot;
                от ООО &quot;Инвойс Маркет&quot; для участников в роли поставщиков
            </Label>
            <div className={styles.commercialTermItems}>
                {invoiceMarketCommercialTerms.map((element, index) => (
                    <CommercialTerm key={index} {...element} />
                ))}
            </div>
            <Text component='div' textSize='14' className={styles.note}>
                * В случае если участник заключил лицензионный договор с ООО «Факторин» в первый раз
                не ранее 18.05.2021 г., для участника устанавливается ознакомительный период
                до 01.09.2021 г., в течение которого участнику предоставляется право
                использования продукта платформы «Уступка с верификацией по ЭДО от
                ООО «Инвойс Маркет» для участников в роли поставщиков» для ознакомления с
                функциональными возможностями ФАКТОРИН, при этом плата (лицензионный платеж) за
                использование ФАКТОРИН в данный период не взимается.<br />
                Начиная со следующего дня после окончания указанного ознакомительного периода
                или после подключения любого другого продукта платформы участник вправе
                использовать ФАКТОРИН только при условии предварительной оплаты
                лицензионного вознаграждения в порядке, указанном в лицензионном договоре
                заключенным участником с ООО «Факторин».
            </Text>
        </GridColumn>
    </GridRow>
);

const sberComercialTerms = [
    {
        title: '14 400 ₽/год',
        description: '',
    },
];

const SberCommercialTerms = () => (
    <GridRow className={styles.commercialTerms}>
        <GridColumn size='8'>
            <Label component='h3' weight='demibold' textSize='21-24'>
                Специальные условия для  участников, которые  подключаются к платформе в
                роли Поставщиков или Покупателей:
            </Label>
            <div className={styles.commercialTermItems}>
                {sberComercialTerms.map((element, index) => (
                    <CommercialTerm key={index} {...element} />
                ))}
            </div>
        </GridColumn>
    </GridRow>
);

const MtsCommercialTerms = () => <></>;

const commercialTermsComponents = {
    '/magnit': TanderCommercialTerms,
    '/onboarding': TanderCommercialTerms,
    '/invoicemarket': InvoiceMarketCommercialTerms,
    '/sberfactoring': SberCommercialTerms,
    '/mts': MtsCommercialTerms,
};

export const Preamble = ({ children }) => {
    const { pathname } = useLocation();
    const CommercialTermsComponent = commercialTermsComponents[pathname] || DefaultCommercialTerms;
    return (
        <div className={styles.preamble}>
            {children}
            <GridRow>
                <GridColumn size={6}>
                    <Label component='h3' weight='demibold' textSize='21-24'>
                        Вам понадобится:
                    </Label>
                    <Text component='ul' textSize='18'>
                        <ul>
                            <Tooltip
                                component='li'
                                style={{ display: 'inline-block' }}
                                tooltip={eSigTooltip}
                            >
                                Электронная цифровая подпись&nbsp;
                                <Icon className={commonStyles.infoIcon} icon={help} />
                            </Tooltip>
                            <Tooltip
                                component='li'
                                style={{ display: 'inline-block' }}
                                tooltip={proxyTooltip}
                            >
                                Доверенность на подписание документов&nbsp;
                                <Icon className={commonStyles.infoIcon} icon={help} />
                            </Tooltip>
                        </ul>
                    </Text>
                </GridColumn>
                <GridColumn size={6}>
                    <PublicDocumentsList />
                </GridColumn>
            </GridRow>
            <CommercialTermsComponent />
            <hr />
            <div className={styles.heading}>
                <Label weight='medium' textSize='32' component='h2'>
                    Процесс подключения:
                </Label>
                <Link href='mailto:help@factorin.io' icon={help} className={styles.link}>
                    help@factorin.io
                </Link>
            </div>
            <RegistrationStep step={0} />
            <Label textSize='32' component='h2' className={styles.step1}>
                Шаг 1.&nbsp;
                <Label weight='demibold' textSize='32' component='span'>
                    Регистрация
                </Label>
            </Label>
        </div>
    );
};
